import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getEventDetail } from "../api/event.api";
import type { EventDetailForAdminResponse } from "../types/event.types";

interface UseGetDetailEventReturn {
  event: EventDetailForAdminResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useGetDetailEvent = (
  id?: string,
  initialEvent?: EventDetailForAdminResponse | null,
): UseGetDetailEventReturn => {
  const [event, setEvent] = useState<EventDetailForAdminResponse | null>(initialEvent ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEventDetail = useCallback(async () => {
    if (!id) {
      setEvent(initialEvent ?? null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const detail = await getEventDetail(id);
      setEvent(detail);
    } catch (requestError: unknown) {
      setError(getApiErrorMessage(requestError, "Failed to load event detail"));
      if (!initialEvent) {
        setEvent(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id, initialEvent]);

  useEffect(() => {
    void fetchEventDetail();
  }, [fetchEventDetail]);

  return {
    event,
    loading,
    error,
    refetch: fetchEventDetail,
  };
};
